// ScanningSessionLogView page
import React, { useState } from "react";
import { Container, Row, Col, Button } from "reactstrap";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import styles from "../../Library/AdverseEvent/css/adverseEvent.module.scss";
// import tabStyles from "../../../pages/PrintLabel/Css/labeltypetabs.module.scss";
import tabStyles from "../../PrintLabel/MainPrintListTabs/css/labeltypetabs.module.scss";
import ac from "../css/scanning.module.scss";
import CommonTableIndexWise from "../../../Components/Common/CommonTableIndexWise";
import { ACCESSIONING_PATHS, SCANNING_PATHS } from "Routes/paths";
import {
  useScanningScanSampleCollectionService,
  useScanningScanSampleSeperationService,
  useScanningSessionService,
} from "modules/ScanningApp/api/useScanningSession";
import { formatDateTime } from "helpers/crf.utils";
import { formatDate } from "common/service/CommonService";
import CommonSearch from "Components/Common/CommonSearch";
import CommonButton from "Components/Common/CommonButton";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import { usePermission } from "context/PermissionContext";
import { getSessionLogColumns, formatTime24Hour, renderDeviationBadge } from "modules/Session/Session/helper/sessionlogconstant";

const ScanningSessionLogView: React.FC = () => {
  document.title = "Session Log | ZENTIXS VISTA";
  
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isScanning = location.pathname.startsWith("/scanning");
  const listPath = isScanning ? SCANNING_PATHS.VIEW : ACCESSIONING_PATHS.LIST;
  const { hasPermission } = usePermission()
  const activetab = hasPermission(PERMISSION_MODULES.SESSION, PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER) ? "subjectToVacutainer" : "vacutainerToAliquot";

  const [searchText, setSearchText] = useState<string>("");
  const [activeTab, setActiveTab] = useState(activetab);

  const [currentPageNumber, setCurrentPageNumber] = useState(1);

  // Responsive Width Hook
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobileOrTablet = windowWidth <= 1024;

  const { SelectQuery: sessionQuery } = useScanningSessionService({
    sessionid: id ? parseInt(id) : 0,
  });

  const sessionData = sessionQuery?.data?.data?.[0];

  const stats = [
    {
      title: "SUBJECTS ACCESSIONED",
      value: `${sessionData?.completed_subjects || 0} / ${sessionData?.total_subjects || sessionData?.totalsubjects || 0}`,
      icon: <i className="ri-group-line" />,
      bgColor: "#ecfdf5",
      iconColor: "#10b981",
    },
    {
      title: "VACUTAINERS MATCHED",
      value: `${sessionData?.completed_vaccutainers || 0} / ${sessionData?.total_vaccutainers || sessionData?.totalsubjects || 0}`,
      icon: <i className="ri-test-tube-line" />,
      bgColor: "#fff7ed",
      iconColor: "#f59e0b",
    },
    {
      title: "ALIQUOTS SEPARATED",
      value: `${sessionData?.completed_aliquots || 0} / ${sessionData?.total_aliquots_count || sessionData?.totalsubjects * sessionData?.total_aliquots || sessionData?.total_aliquots || 0}`,
      icon: <i className="ri-flask-line" />,
      bgColor: "#ecfdf5",
      iconColor: "#10b981",
    },
  ];

  const { SelectAllQuery: collectionQuery } = useScanningScanSampleCollectionService({
    sessionid: id ? parseInt(id) : 0,
    searchtext: searchText,
    currentpage: currentPageNumber,
    pagesize: 10,
  });

  const { SelectAllQuery: separationQuery } = useScanningScanSampleSeperationService({
    sessionid: id ? parseInt(id) : 0,
    searchtext: searchText,
    currentpage: currentPageNumber,
    pagesize: 10,
  });

  const activeQuery =
    activeTab === "subjectToVacutainer" ? collectionQuery : separationQuery;
  const logData = activeQuery?.data?.data || [];
  const totalRecords = logData?.[0]?.total_count || 0;

  const columns = getSessionLogColumns(activeTab, ac);

  const onBack = () => {
    navigate(listPath);
  };

  const onSearch = (text: string | null) => {
    setSearchText(text ?? "");
    setCurrentPageNumber(1);
  };
  const permission1 = hasPermission(PERMISSION_MODULES.SESSION, PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER)
  const permission2 = hasPermission(PERMISSION_MODULES.SESSION, PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT)

  return (
    <div className={`${styles.dashboardContainer} p-0 pb-3`}>
      <Container fluid>
        {/* Page Header */}
        {isMobileOrTablet ? (
          <header className={ac.mobileLogHeader}>
            <button
              type="button"
              className={ac.mobileHeaderBackBtn}
              onClick={onBack}
            >
              <i className="ri-arrow-left-line text-dark fs-18"></i>
            </button>
            <div className={ac.mobileHeaderTitleContainer}>
              <div className="d-flex align-items-center gap-2">
                <h1 className={ac.mobileHeaderTitle}>Log: {sessionData?.sessionno || sessionData?.sessionid || id}</h1>
                <span className="badge bg-success bg-opacity-10 text-success rounded-pill px-2 py-0.5 fw-600" style={{ fontSize: "10px" }}>
                  COMPLETED
                </span>
              </div>
              <p className={ac.mobileHeaderSubtitle}>
                {sessionData?.studycode} | P-{sessionData?.period_number} | T-{sessionData?.timepoint_number}
              </p>
            </div>
          </header>
        ) : (
          <div className="d-flex justify-content-between align-items-center mt-4 mb-4 px-2">
            <div className="d-flex align-items-center gap-3">
              <Button
                color="light"
                className="bg-white border rounded-3 px-2 py-1 shadow-sm"
                onClick={onBack}
              >
                <i className="ri-arrow-left-line text-dark fs-18"></i>
              </Button>
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <h5 className={`mb-0 ${styles.pageHeaderTitle}`}>
                    Chronological Scan Log:{" "}
                    {sessionData?.sessionno || sessionData?.sessionid || id}
                  </h5>
                  <span
                    className="badge bg-success bg-opacity-10 text-success rounded-pill px-3 py-1 fw-600"
                    style={{ fontSize: "11px" }}
                  >
                    COMPLETED
                  </span>
                </div>
                <p className={styles.pageHeaderSubtitle}>
                  {sessionData?.studycode} | Period {sessionData?.period_number} |
                  Timepoint {sessionData?.timepoint_number}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stat Cards */}
        {isMobileOrTablet ? (
          <div className={ac.mobileStatsRow}>
            {stats.map((stat, idx) => (
              <div key={idx} className={ac.mobileStatCard}>
                <span className={ac.mobileStatLabel}>
                  {stat.title.replace("ACCESSIONED", "").replace("MATCHED", "").replace("SEPARATED", "").trim()}
                </span>
                <span className={ac.mobileStatValue}>{stat.value}</span>
              </div>
            ))}
          </div>
        ) : (
          <Row className="g-3 mb-4 px-1">
            {stats.map((stat, idx) => (
              <Col key={idx} md={4}>
                <div className={styles.statCard}>
                  <div
                    className={styles.iconWrapper}
                    style={{
                      backgroundColor: stat.bgColor,
                      color: stat.iconColor,
                    }}
                  >
                    {stat.icon}
                  </div>
                  <div className={styles.label}>{stat.title}</div>
                  <div className={styles.value}>{stat.value}</div>
                </div>
              </Col>
            ))}
          </Row>
        )}

        {/* Log Panel */}
        <div className={styles.listPanel}>
          {isMobileOrTablet ? (
            <div className="p-3 pb-2">
              <CommonSearch
                onSearch={onSearch}
                searchText={searchText}
                placeholder="Search Barcode..."
              />
            </div>
          ) : (
            <div className={styles.panelHeader}>
              <div>
                <h2>
                  <i className="ri-list-check-2 text-primary fs-18 me-2"></i>
                  Chronological Scan Log
                </h2>
                <p
                  className="text-muted mb-0 mt-1"
                  style={{ fontSize: "12px", fontWeight: 500 }}
                >
                  Detailed real-time log of all barcodes scanned and events
                  recorded during this processing session.
                </p>
              </div>
              <CommonSearch
                onSearch={onSearch}
                searchText={searchText}
                placeholder="Search Barcode..."
              />
            </div>
          )}

          {/* Navigation Tabs */}
          {isMobileOrTablet ? (
            <div className={ac.logWorkflowCards}>
              {permission1 && (
                <div
                  className={`${ac.logWorkflowCard} ${activeTab === "subjectToVacutainer" ? ac.active : ""}`}
                  onClick={() => {
                    setActiveTab("subjectToVacutainer");
                    setSearchText("");
                    setCurrentPageNumber(1);
                  }}
                >
                  <div className={ac.logCardIcon}>
                    <i className="ri-user-shared-line"></i>
                  </div>
                  <div className={ac.logCardText}>
                    <h6 className={ac.logCardTitle}>Subject to Vacutainer</h6>
                    <span className={ac.logCardDesc}>
                      Scan subject and match to corresponding vacutainer
                    </span>
                  </div>
                </div>
              )}

              {permission2 && (
                <div
                  className={`${ac.logWorkflowCard} ${activeTab === "vacutainerToAliquot" ? ac.active : ""}`}
                  onClick={() => {
                    setActiveTab("vacutainerToAliquot");
                    setSearchText("");
                    setCurrentPageNumber(1);
                  }}
                >
                  <div className={ac.logCardIcon}>
                    <i className="ri-flask-line"></i>
                  </div>
                  <div className={ac.logCardText}>
                    <h6 className={ac.logCardTitle}>Vacutainer to Aliquot</h6>
                    <span className={ac.logCardDesc}>
                      Scan vacutainer and separate into aliquot samples
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className={tabStyles.tabContainer}>
              <CommonButton
                module={PERMISSION_MODULES.SESSION}
                action={PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER}
                type="button"
                className={`${tabStyles.tabBtn} ${activeTab === "subjectToVacutainer" ? tabStyles.active : ""}`}
                onClick={() => {
                  setActiveTab("subjectToVacutainer");
                  setSearchText("");
                  setCurrentPageNumber(1);   // ← reset page
                }}
              >
                Subject to Vacutainer
              </CommonButton>
              <CommonButton
                module={PERMISSION_MODULES.SESSION}
                action={PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}
                type="button"
                className={`${tabStyles.tabBtn} ${activeTab === "vacutainerToAliquot" ? tabStyles.active : ""}`}
                onClick={() => {
                  setActiveTab("vacutainerToAliquot");
                  setSearchText("");
                  setCurrentPageNumber(1);   // ← reset page
                }}
              >
                Vacutainer to Aliquot
              </CommonButton>
            </div>
          )}

          <div className="px-2 pb-2 mt-3">
            {isMobileOrTablet ? (
              <React.Fragment>
                {(collectionQuery.isFetching || separationQuery.isFetching || sessionQuery.isFetching) ? (
                  <div className="text-center py-5">
                    <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span>
                    <span className="ms-2 text-muted">Loading scan history...</span>
                  </div>
                ) : (
                  <div className={ac.mobileLogList}>
                    {logData.length === 0 ? (
                      <div className="text-center py-4 text-muted bg-white border rounded-3 p-4">
                        No scan logs found.
                      </div>
                    ) : (
                      logData.map((row: any, idx: number) => {
                        const isValid = row.result === "valid";
                        let datePart = "";
                        let timePart = "";

                        if (activeTab === "subjectToVacutainer") {
                          datePart = row.dosingscheduledate ? formatDate(row.dosingscheduledate) : "-";
                          timePart = row.dosingscheduletime ? formatTime24Hour(row.dosingscheduletime) : "-";
                        } else {
                          const scanDateFormatted = formatDateTime(row.scandate);
                          const [dPart, ...tParts] = scanDateFormatted.split(" ");
                          datePart = dPart;
                          timePart = tParts.join(" ");
                        }

                        return (
                          <div key={idx} className={ac.mobileLogCard}>
                            <div className={ac.logCardHeader}>
                              <div className={ac.logCardTime}>
                                <i className="ri-calendar-line me-1 text-primary"></i>
                                <span className="text-muted me-1" style={{ fontSize: "11px" }}>
                                  {activeTab === "subjectToVacutainer" ? "Sched. Date:" : "Scan Date:"}
                                </span>
                                <strong className="text-dark me-2" style={{ fontSize: "11.5px" }}>{datePart}</strong>
                                <span className="mx-1 text-muted">|</span>
                                <i className="ri-time-line ms-1 me-1 text-primary"></i>
                                <span className="text-muted me-1" style={{ fontSize: "11px" }}>
                                  {activeTab === "subjectToVacutainer" ? "Sched. Time:" : "Scan Time:"}
                                </span>
                                <strong className="text-dark" style={{ fontSize: "11.5px" }}>{timePart}</strong>
                              </div>
                              <span
                                className={`badge ${isValid ? "bg-success" : "bg-danger"} bg-opacity-10 ${isValid ? "text-success" : "text-danger"} border ${isValid ? "border-success" : "border-danger"} border-opacity-25 px-2 py-1 text-uppercase`}
                                style={{ fontSize: "11px", fontWeight: "600" }}
                              >
                                {row.result}
                              </span>
                            </div>

                            <div className={ac.logCardBody}>
                              {activeTab === "subjectToVacutainer" ? (
                                <React.Fragment>
                                  {/* Subject No & Dosing Station */}
                                  <div className="d-flex justify-content-between mb-0 small text-muted">
                                    <span>Subject No: <strong>{row.subjectno || "-"}</strong></span>
                                    <span>Station: <strong>{row.dosingstationno || "-"}</strong></span>
                                  </div>

                                  {/* Connection between Subject and Vacutainer */}
                                  <div className={ac.logCardConnection + " mb-0"}>
                                    <div className={ac.logCardItem}>
                                      <span className={`${ac.itemLabel} text-start`} >Subject</span>
                                      <span className={`${ac.itemValue} text-start`}>{row.subjectbarcode || "-"}</span>
                                    </div>
                                    <div className={ac.connectionArrow}>
                                      <i className="ri-arrow-right-line"></i>
                                    </div>
                                    <div className={ac.logCardItem}>
                                      <span className={`${ac.itemLabel} text-end`} >Vacutainer</span>
                                      <span className={`${ac.itemValue} text-end`}>{row.vaccutainerbarcode || "-"}</span>
                                    </div>
                                  </div>

                                  {/* Collection Time & Deviation */}
                                  <div className="d-flex justify-content-between align-items-center mb-0 mt-0 pt-2 border-top">
                                    <div className="small">
                                      <span className="text-muted">Collection: </span>
                                      <strong>{row.samplecollectiontime ? formatTime24Hour(row.samplecollectiontime) : "-"}</strong>
                                    </div>
                                    <div className="d-flex align-items-center gap-1 small">
                                      <span className="text-muted">Deviation:</span>
                                      {renderDeviationBadge(row)}
                                    </div>
                                  </div>

                                  {(row.deviationstatus || row.deviationreason) && (
                                    <div className="d-flex flex-column gap-1 pt-1 mt-1 border-top small">
                                      {row.deviationstatus && (
                                        <div className="d-flex align-items-center justify-content-between">
                                          <span className="text-muted">Dev. Status:</span>
                                           <span className={`badge ${
                                             row.deviationstatus.toLowerCase().includes("exceed") || row.deviationstatus.toLowerCase().includes("missed") || row.deviationstatus.toLowerCase().includes("error")
                                               ? "bg-danger bg-opacity-10 text-danger border-danger"
                                               : row.deviationstatus.toLowerCase().includes("early")
                                               ? "bg-warning bg-opacity-10 text-warning border-warning"
                                               : row.deviationstatus.toLowerCase().includes("within") || row.deviationstatus.toLowerCase().includes("normal")
                                               ? "bg-success bg-opacity-10 text-success border-success"
                                               : "bg-secondary bg-opacity-10 text-secondary border-secondary"
                                           } border border-opacity-25 px-2 py-0.5`} style={{ fontSize: "10.5px" }}>
                                             {row.deviationstatus}
                                           </span>
                                        </div>
                                      )}
                                      {row.deviationreason && (
                                        <div className="d-flex align-items-center justify-content-between">
                                          <span className="text-muted">Dev. Reason:</span>
                                          <span className="text-dark fw-medium text-truncate" style={{ maxWidth: "160px" }}>{row.deviationreason}</span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </React.Fragment>
                              ) : (
                                <div className={ac.logCardConnection}>
                                  <div className={ac.logCardItem}>
                                    <span className={`${ac.itemLabel} text-start`} >Vacutainer</span>
                                    <span className={`${ac.itemValue} text-start`}>{row.vaccutainerbarcode || "-"}</span>
                                  </div>
                                  <div className={ac.connectionArrow}>
                                    <i className="ri-arrow-right-line"></i>
                                  </div>
                                  <div className={ac.logCardItem}>
                                    <span className={`${ac.itemLabel} text-end`} >Aliquot</span>
                                    <span className={`${ac.itemValue} text-end`}>{row.aliquotbarcode || "-"}</span>
                                  </div>
                                </div>
                              )}

                              {row.resultmessage && (
                                <div className={`${ac.logCardMessage} ${isValid ? ac.msgSuccess : ac.msgDanger}`}>
                                  <i className={isValid ? "ri-checkbox-circle-line me-1" : "ri-error-warning-line me-1"}></i>
                                  {row.resultmessage}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}

                    {/* Mobile Pagination */}
                    {totalRecords > 10 && (
                      <div className="d-flex justify-content-between align-items-center mt-3 px-2">
                        <Button
                          color="light"
                          size="sm"
                          disabled={currentPageNumber === 1}
                          onClick={() => setCurrentPageNumber(prev => Math.max(prev - 1, 1))}
                          className="bg-white border rounded-2 px-3 py-1 text-dark fs-12 fw-600"
                        >
                          <i className="ri-arrow-left-s-line me-1" /> Prev
                        </Button>
                        <span className="text-muted fs-12">
                          Page <strong>{currentPageNumber}</strong> of <strong>{Math.ceil(totalRecords / 10)}</strong>
                        </span>
                        <Button
                          color="light"
                          size="sm"
                          disabled={currentPageNumber >= Math.ceil(totalRecords / 10)}
                          onClick={() => setCurrentPageNumber(prev => prev + 1)}
                          className="bg-white border rounded-2 px-3 py-1 text-dark fs-12 fw-600"
                        >
                          Next <i className="ri-arrow-right-s-line ms-1" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            ) : (
              <CommonTableIndexWise
                columns={columns}
                data={logData}
                pageSize={10}
                currentPage={currentPageNumber}
                totalRecords={totalRecords}
                onPageChange={setCurrentPageNumber}
                hideAction={true}
                loading={
                  collectionQuery.isFetching ||
                  separationQuery.isFetching ||
                  sessionQuery.isFetching
                }
              />
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ScanningSessionLogView;
